import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, TextField, Button, Typography, Alert, CircularProgress, Link
} from '@mui/material';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import AuthLayout from '../components/AuthLayout';
import { login, clearError, clearMfa } from '../store/authSlice';
import { generateCodeVerifier, generateCodeChallenge, generateState } from '../utils/pkce';
import ValidatedField, { validEmail } from '../components/motion/ValidatedField';

const AUTH_SERVICE_URL = process.env.REACT_APP_AUTH_SERVICE_URL || 'https://auth.sentrivox.com';
const SSO_REDIRECT_URI = process.env.REACT_APP_SSO_REDIRECT_URI || 'https://cloud.sentrivox.com/auth/callback';
// sessionStorage (not localStorage): the verifier only needs to survive the
// redirect round trip to auth.sentrivox.com and back, in this same tab.
const PKCE_VERIFIER_KEY = 'sentrivox_sso_pkce_verifier';
const PKCE_STATE_KEY = 'sentrivox_sso_state';

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, loading, error, mfaRequired, mfaEnrollRequired, mfaToken } = useSelector((state) => state.auth);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [shakePassword, setShakePassword] = useState(false);

  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (mfaRequired && mfaToken) {
      navigate('/mfa-verify', { state: { mfaToken }, replace: true });
    }
  }, [mfaRequired, mfaToken, navigate]);

  useEffect(() => { dispatch(clearError()); }, [dispatch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(login({ email, password }));
  };

  const handleSentrivoxSso = async () => {
    const verifier = generateCodeVerifier();
    const challenge = await generateCodeChallenge(verifier);
    const state = generateState();
    // sessionStorage, not component state: the browser fully navigates away
    // to auth.sentrivox.com and back, so nothing in memory survives the round trip.
    sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
    sessionStorage.setItem(PKCE_STATE_KEY, state);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: 'cloud',
      redirect_uri: SSO_REDIRECT_URI,
      code_challenge: challenge,
      code_challenge_method: 'S256',
      state,
    });
    window.location.href = `${AUTH_SERVICE_URL}/authorize?${params.toString()}`;
  };

  const isUpgradeRequired = error?.upgradeRequired;
  const errorMessage = typeof error === 'string' ? error : error?.message;

  useEffect(() => {
    if (errorMessage && !isUpgradeRequired) setShakePassword(true);
  }, [error, errorMessage, isUpgradeRequired]);

  return (
    <AuthLayout title="Sign in to Sentrivox Cloud" subtitle="Cloud security posture for your connected accounts">
      {isUpgradeRequired && (
        <Alert
          severity="warning"
          icon={<UpgradeIcon />}
          sx={{ mb: 2 }}
          onClose={() => dispatch(clearError())}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Plan Upgrade Required
          </Typography>
          <Typography variant="body2">
            {errorMessage || 'Your current plan does not include cloud security features. Please upgrade to CloudFusion or higher.'}
          </Typography>
          {error?.currentPlan && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Current plan: {error.currentPlan}
            </Typography>
          )}
        </Alert>
      )}

      {mfaEnrollRequired && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => dispatch(clearMfa())}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Two-factor authentication isn't set up yet
          </Typography>
          <Typography variant="body2">
            Sign in to the Sentrivox portal and set it up under{' '}
            {/* The backend's enrollPortal still points at the retired portal.bitnetit.in on the live revision */}
            <a href="https://portal.sentrivox.com/profile-settings" target="_blank" rel="noreferrer" style={{ color: 'inherit', fontWeight: 600 }}>
              Profile settings
            </a>, then come back and sign in here.
          </Typography>
        </Alert>
      )}

      {errorMessage && !isUpgradeRequired && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
          {errorMessage}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <ValidatedField
          fullWidth label="Email" type="email" margin="normal" validate={validEmail}
          value={email} onChange={(e) => setEmail(e.target.value)}
          required autoFocus autoComplete="email"
        />
        <TextField
          fullWidth label="Password" type="password" margin="normal"
          value={password} onChange={(e) => setPassword(e.target.value)}
          required autoComplete="current-password"
          className={shakePassword ? 'svx-shake' : undefined}
          onAnimationEnd={() => setShakePassword(false)}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
          {/* portal.sentrivox.com is the canonical password-reset flow: portal,
              cloud, and cm share one users collection and one password field, so
              resetting there resets it everywhere rather than building a third
              separate reset+email pipeline here. */}
          <Link href="https://portal.sentrivox.com/forgot-password" variant="body2" underline="hover">
            Forgot Password?
          </Link>
        </Box>
        <Button
          type="submit" fullWidth variant="contained" size="large"
          disabled={loading} sx={{ mt: 3, mb: 1 }}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : undefined}
        >
          {loading ? 'Signing In...' : 'Sign In'}
        </Button>
      </form>

      <Box sx={{ display: 'flex', alignItems: 'center', my: 2, color: 'text.secondary' }}>
        <Box sx={{ flex: 1, borderBottom: '1px solid', borderColor: 'divider' }} />
        <Typography variant="caption" sx={{ mx: 1.5 }}>or</Typography>
        <Box sx={{ flex: 1, borderBottom: '1px solid', borderColor: 'divider' }} />
      </Box>

      {/* Additive, not a replacement for the form above (v4 §7) - portal, cloud, and cm
          share one users collection, so this is an alternate way to authenticate the same
          account, not a separate one. */}
      <Button
        fullWidth variant="outlined" size="large"
        onClick={handleSentrivoxSso}
      >
        Continue with Sentrivox
      </Button>
    </AuthLayout>
  );
}
